import React, { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { Modal, Button } from "antd";

const DAYS = ["su", "mo", "tu", "we", "th", "fr", "sa"];
const DAY_ABBR = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const getTaskKey = (t) => t?.dailyTaskId ?? t?.taskId ?? t?.DailyTask?.id ?? t?.id;

// Scenario identity is the scenario it points to (not the per-day activity id),
// so the same scenario placed on several days/times is recognized as "the same".
const getScenarioKey = (t) => t?.scenarioId ?? t?.Scenario?.id ?? null;

// "Source time" signature used to decide whether two scenario instances share a time.
// Only absolute notifications are time-matched; relative ones are ignored (return null
// so they never count as occupying a cell).
const timeKeyOf = (t) => {
  const notificationType = t?.notificationType || "absolute";
  if (notificationType !== "absolute") return null;
  const h = t?.remindHour;
  const m = t?.remindMinute;
  if (!Number.isInteger(h) || !Number.isInteger(m)) return null;
  return `${h}:${m}`;
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

export default function WeekDayPickerModal({
  open,
  onCancel,
  onApply,
  weeklySchedules = [],
  allPlanTasks = [],
  currentActivity,
  readOnly = false,
  position,
  isScenario = false,
}) {
  const totalWeeks = weeklySchedules.length || 0;
  const totalDays = totalWeeks * 7;

  const keyOf = useCallback(
    (t) => (isScenario ? getScenarioKey(t) : getTaskKey(t)),
    [isScenario]
  );

  const currentKey = keyOf(currentActivity);
  // Scenarios are single-day; only their time differentiates instances.
  const duration = isScenario ? 1 : clamp(Number(currentActivity?.duration || 1), 1, 999);

  // For scenarios the source carries a fixed time; only same-scenario + same-time
  // instances are surfaced as "busy". Same scenario at another time is invisible here.
  const currentTimeKey = isScenario ? timeKeyOf(currentActivity) : null;

  const same = useMemo(() => {
    if (!currentKey) return [];
    return (allPlanTasks || []).filter((t) => {
      if (keyOf(t) !== currentKey) return false;
      if (!isScenario) return true;
      return currentTimeKey != null && timeKeyOf(t) === currentTimeKey;
    });
  }, [allPlanTasks, currentKey, keyOf, isScenario, currentTimeKey]);

  const [draftRemoved, setDraftRemoved] = useState(() => new Set());
  const [draftAddedStarts, setDraftAddedStarts] = useState(() => new Map());
  const [hoverAbs, setHoverAbs] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDraftRemoved(new Set());
    setDraftAddedStarts(new Map());
    setHoverAbs(null);
  }, [open, currentKey, currentTimeKey]);

  const normWeek = (x) =>
    Number.isInteger(x?.originalWeek)
      ? x.originalWeek
      : Number.isInteger(x?.weekIndex)
      ? x.weekIndex
      : 0;

  const normDayIndex = (x) => {
    const day = x?.originalDay ?? x?.day;
    return DAYS.indexOf(day);
  };

  const absSetFor = useCallback(
    (startAbs, dur) => {
      const set = new Set();
      if (!totalDays) return set;
      const steps = Math.min(dur, totalDays);
      for (let i = 0; i < steps; i++) set.add((startAbs + i) % totalDays);
      return set;
    },
    [totalDays]
  );

  const instancesByStartAbs = useMemo(() => {
    const out = new Map();
    if (!totalWeeks || !currentKey) return out;

    const seen = new Set();

    for (const inst of same || []) {
      const activityId = inst?.weeklyScheduleActivityId ?? inst?.id;

      const uniq =
        inst.weeklyScheduleActivityId ??
        `${keyOf(inst)}:${inst.originalWeek}:${inst.originalDay}:${inst.duration}:${activityId}`;

      if (seen.has(uniq)) continue;
      seen.add(uniq);

      const w = normWeek(inst);
      const d = normDayIndex(inst);
      const dur = clamp(Number(inst.duration || 1), 1, 999);

      if (w < 0 || w >= totalWeeks || d < 0) continue;

      const startAbs = w * 7 + d;

      out.set(startAbs, {
        kind: "existing",
        startAbs,
        weekIndex: w,
        dayIndex: d,
        day: DAYS[d],
        duration: dur,
        activityId,
        raw: inst,
      });
    }

    return out;
  }, [same, currentKey, totalWeeks, keyOf]);

  const effectiveInstances = useMemo(() => {
    const list = [];
    for (const inst of instancesByStartAbs.values()) {
      if (draftRemoved.has(inst.activityId)) continue;
      list.push(inst);
    }
    for (const draft of draftAddedStarts.values()) list.push(draft);
    return list;
  }, [instancesByStartAbs, draftRemoved, draftAddedStarts]);

  const paintMap = useMemo(() => {
    const map = new Map(); // abs -> { state, startAbs }
    if (!totalWeeks || !currentKey || !totalDays) return map;

    for (const inst of effectiveInstances) {
      const startAbs = inst.startAbs;
      const dur = clamp(Number(inst.duration || 1), 1, 999);

      const steps = Math.min(dur, totalDays);
      for (let i = 0; i < steps; i++) {
        const abs = (startAbs + i) % totalDays;
        if (i === 0) map.set(abs, { state: "start", startAbs });
        else if (!map.has(abs)) map.set(abs, { state: "cont", startAbs });
      }
    }

    return map;
  }, [effectiveInstances, totalWeeks, totalDays, currentKey]);

  const getPlaceReason = useCallback(
    (startAbs, dur) => {
      if (!totalDays) return "No plan days";
      if (startAbs < 0 || startAbs >= totalDays) return "Out of plan";
      if (dur > totalDays) return "Doesn't fit: duration exceeds plan cycle";

      const steps = Math.min(dur, totalDays);
      for (let i = 0; i < steps; i++) {
        const abs = (startAbs + i) % totalDays;
        if (paintMap.has(abs)) return "Overlaps existing instance of this task";
      }
      return null;
    },
    [paintMap, totalDays]
  );

  const canPlaceAt = useCallback((startAbs, dur) => !getPlaceReason(startAbs, dur), [getPlaceReason]);

  const disabledStartAbsSet = useMemo(() => {
    const set = new Set();
    if (!totalDays) return set;

    for (let abs = 0; abs < totalDays; abs++) {
      if (paintMap.has(abs)) continue;
      const reason = getPlaceReason(abs, duration);
      if (reason) set.add(abs);
    }
    return set;
  }, [totalDays, paintMap, getPlaceReason, duration]);

  const hoverPreview = useMemo(() => {
    if (hoverAbs == null || !totalDays) return null;

    const painted = paintMap.get(hoverAbs);
    if (painted) {
      const startAbs = painted.startAbs;
      const inst = draftAddedStarts.get(startAbs) ?? instancesByStartAbs.get(startAbs);
      const dur = clamp(Number(inst?.duration || 1), 1, 999);

      return {
        mode: "remove",
        ok: true,
        absSet: absSetFor(startAbs, dur),
        startAbs,
      };
    }

    if (!canPlaceAt(hoverAbs, duration)) {
      return { mode: "disabled", ok: false, absSet: null, startAbs: hoverAbs };
    }

    return {
      mode: "add",
      ok: true,
      absSet: absSetFor(hoverAbs, duration),
      startAbs: hoverAbs,
    };
  }, [hoverAbs, totalDays, paintMap, canPlaceAt, duration, absSetFor, draftAddedStarts, instancesByStartAbs]);

  // Build a fresh "draft" instance for a start day, cloning the source
  // activity's settings. Shared by single-cell placement and "Add to all days".
  const makeDraft = useCallback(
    (abs) => ({
      kind: "draft",
      startAbs: abs,
      weekIndex: Math.floor(abs / 7),
      dayIndex: abs % 7,
      day: DAYS[abs % 7],
      duration,

      remind: true,
      remindHour: currentActivity?.remindHour,
      remindMinute: currentActivity?.remindMinute,

      dailyTaskId: currentActivity?.dailyTaskId ?? currentActivity?.DailyTask?.id ?? currentActivity?.taskId,
      DailyTask: currentActivity?.DailyTask,
      raw: currentActivity,

      scenarioId: currentActivity?.scenarioId ?? currentActivity?.Scenario?.id,

      rescheduling: currentActivity?.rescheduling,
      lockType: currentActivity?.lockType,
      lockHours: currentActivity?.lockHours,
      lockMinutes: currentActivity?.lockMinutes,
      lockPeriod: currentActivity?.lockPeriod,

      // preserve the source's notification settings on the cloned instance
      notificationType: currentActivity?.notificationType,
      triggerSubjectProfileFieldId: currentActivity?.triggerSubjectProfileFieldId,
      triggerOffsetMinutes: currentActivity?.triggerOffsetMinutes,
      // Multiple reminders belong to tasks. A scenario's base time is when it
      // enters the chatbot, so scenario copies must not carry accidental extras.
      extraReminders: isScenario ? [] : currentActivity?.extraReminders || null,

      linkedPage: currentActivity?.linkedPage,
      status: currentActivity?.status,
      internalId: currentActivity?.internalId,
    }),
    [duration, currentActivity, isScenario]
  );

  const handleCellClick = useCallback(
    (abs) => {
      if (readOnly) return;
      if (!paintMap.has(abs) && !canPlaceAt(abs, duration)) return;

      const painted = paintMap.get(abs);

      if (painted) {
        const startAbs = painted.startAbs;

        if (draftAddedStarts.has(startAbs)) {
          const next = new Map(draftAddedStarts);
          next.delete(startAbs);
          setDraftAddedStarts(next);
          return;
        }

        const inst = instancesByStartAbs.get(startAbs);
        if (!inst?.activityId) return;

        const next = new Set(draftRemoved);
        next.add(inst.activityId);
        setDraftRemoved(next);
        return;
      }

      const next = new Map(draftAddedStarts);
      next.set(abs, makeDraft(abs));
      setDraftAddedStarts(next);
    },
    [readOnly, paintMap, canPlaceAt, duration, draftAddedStarts, instancesByStartAbs, draftRemoved, makeDraft]
  );

  // Number of empty days on which a new instance could still START (respecting
  // duration overlap). Drives the "Add to all days" / "Reset selection" toggle.
  const placeableEmptyCount = totalDays - paintMap.size - disabledStartAbsSet.size;
  const allDaysFilled = placeableEmptyCount <= 0;
  // Show "Reset selection" whenever every placeable day is filled — whether by
  // this session's additions or because the task/scenario was already assigned
  // to all (available) days on open; otherwise "Add to all days".
  const showResetToggle = allDaysFilled;
  // Any day currently painted (effective state). Guards against showing an
  // actionable Reset when there is nothing to clear.
  const hasSelection = paintMap.size > 0;

  // "Add to all days": greedily place a draft on every empty placeable day.
  // Occupancy grows as we place, so multi-day tasks space out and days whose
  // duration span would overlap an existing/just-placed instance are skipped
  // (that is acceptable — some days may stay empty because of duration).
  const handleAddAllDays = useCallback(() => {
    if (readOnly || !totalDays) return;

    const occupied = new Set(paintMap.keys());
    const next = new Map(draftAddedStarts);
    const steps = Math.min(duration, totalDays);

    for (let abs = 0; abs < totalDays; abs++) {
      if (occupied.has(abs)) continue;

      let fits = true;
      for (let i = 0; i < steps; i++) {
        if (occupied.has((abs + i) % totalDays)) { fits = false; break; }
      }
      if (!fits) continue;

      next.set(abs, makeDraft(abs));
      for (let i = 0; i < steps; i++) occupied.add((abs + i) % totalDays);
    }

    setDraftAddedStarts(next);
  }, [readOnly, totalDays, paintMap, draftAddedStarts, duration, makeDraft]);

  // "Reset selection" has two shapes depending on how the plan got filled:
  //  - If this session added instances, drop only those; days that were already
  //    assigned to the plan stay as they were.
  //  - Otherwise the task/scenario was already on all (available) days at open,
  //    so clear everything — remove every existing instance, leaving no day
  //    selected. The toggle then flips back to "Add to all days".
  const handleResetSelection = useCallback(() => {
    if (readOnly) return;

    if (draftAddedStarts.size > 0) {
      setDraftAddedStarts(new Map());
      return;
    }

    const next = new Set(draftRemoved);
    for (const inst of instancesByStartAbs.values()) {
      if (inst.activityId != null) next.add(inst.activityId);
    }
    setDraftRemoved(next);
  }, [readOnly, draftAddedStarts, draftRemoved, instancesByStartAbs]);

  const handleApply = useCallback(() => {
    const patch = {
      removedActivityIds: Array.from(draftRemoved),
      added: Array.from(draftAddedStarts.values()).map((x) => ({
        weekIndex: x.weekIndex,
        day: x.day,
        duration: x.duration,

        remind: x.remind,
        remindHour: x.remindHour,
        remindMinute: x.remindMinute,

        dailyTaskId: x.dailyTaskId,
        scenarioId: x.scenarioId,

        rescheduling: x.rescheduling,
        lockType: x.lockType,
        lockHours: x.lockHours,
        lockMinutes: x.lockMinutes,
        lockPeriod: x.lockPeriod,

        notificationType: x.notificationType,
        triggerSubjectProfileFieldId: x.triggerSubjectProfileFieldId,
        triggerOffsetMinutes: x.triggerOffsetMinutes,
        extraReminders: x.extraReminders || null,

        linkedPage: x.linkedPage,
        status: x.status,
        internalId: x.internalId,
      })),
    };
    onApply?.(patch);
  }, [draftRemoved, draftAddedStarts, onApply]);

  // visuals
  const CELL = 32;
  const GAP = 4;

  const COLOR_START = "#2762F5";
  const COLOR_CONT = "rgba(39, 158, 245, 0.3)";
  const COLOR_EMPTY = "#6C6C8914";

  const DISABLED_BG = "rgba(17, 24, 39, 0.08)";
  const DISABLED_TEXT = "rgba(17, 24, 39, 0.55)";

  const HOVER_REMOVE_START = "rgba(39, 98, 245, 0.55)";
  const HOVER_REMOVE_CONT = "rgba(39, 158, 245, 0.18)";

  const HOVER_ADD_START = "#2B6EF3";
  const HOVER_ADD_CONT = "rgba(43, 110, 243, 0.35)";

  // ---- NEW: stable hover even across gaps (row-level mouse tracking) ----
  const rowRefs = useRef(new Map()); // weekIndex -> element
  const setRowRef = useCallback(
    (week) => (el) => {
      if (el) rowRefs.current.set(week, el);
      else rowRefs.current.delete(week);
    },
    []
  );

  const handleRowMouseMove = useCallback(
    (week) => (e) => {
      if (readOnly) return;

      const el = rowRefs.current.get(week);
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;

      const step = CELL + GAP;
      let dayIndex = Math.floor((x + GAP / 2) / step);
      dayIndex = clamp(dayIndex, 0, 6);

      setHoverAbs(week * 7 + dayIndex);
    },
    [CELL, GAP, readOnly]
  );

  const handleRowMouseLeave = useCallback(() => {
    setHoverAbs(null);
  }, []);

  return (
    <Modal
      rootClassName="week-picker-modal"
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="apply" type="primary" onClick={handleApply} disabled={readOnly}>
          Apply
        </Button>,
      ]}
      closable={false}
      mask={false}
      destroyOnHidden
      getContainer={() => document.body}
      zIndex={3000}
      style={{
        position: "fixed",
        margin: 0,
        top: position?.top ?? 100,
        left: position?.left ?? 100,
      }}
      width={272}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {weeklySchedules.map((ws) => {
          const week = ws.weekIndex;
          return (
            <div key={ws.id || week} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontWeight: 400 }}>{`Week ${week + 1}`}</div>

              {/* row-level hover: keeps hover/preview stable even when cursor is in GAP */}
              <div
                ref={setRowRef(week)}
                onMouseMove={handleRowMouseMove(week)}
                onMouseLeave={handleRowMouseLeave}
                style={{ display: "flex", gap: GAP }}
              >
                {DAYS.map((day, dayIndex) => {
                  const abs = week * 7 + dayIndex;

                  // preview map when hovering so duration is connected on hover too
                  const previewAbsSet = hoverPreview?.absSet || null;
                  const isPreviewing = !!previewAbsSet;
                  const isInPreview = isPreviewing && previewAbsSet.has(abs);

                  const effectivePainted = isInPreview
                    ? {
                        state: abs === hoverPreview?.startAbs ? "start" : "cont",
                        startAbs: hoverPreview?.startAbs,
                      }
                    : paintMap.get(abs);

                  const state = effectivePainted?.state;

                  // disabled is visible always, but NOT inside a valid add preview range
                  const baseDisabledStart = disabledStartAbsSet.has(abs);
                  const isInValidAddPreview = hoverPreview?.mode === "add" && hoverPreview?.ok && isInPreview;
                  const isDisabledStart = baseDisabledStart && !isInValidAddPreview && !state;

                  // neighbors against effective painted (so "bridges" work in hover preview too)
                  const prevAbs = dayIndex > 0 ? abs - 1 : null;
                  const nextAbs = dayIndex < 6 ? abs + 1 : null;

                  const prevEff =
                    prevAbs != null
                      ? isPreviewing && previewAbsSet.has(prevAbs)
                        ? {
                            state: prevAbs === hoverPreview?.startAbs ? "start" : "cont",
                            startAbs: hoverPreview?.startAbs,
                          }
                        : paintMap.get(prevAbs)
                      : null;

                  const nextEff =
                    nextAbs != null
                      ? isPreviewing && previewAbsSet.has(nextAbs)
                        ? {
                            state: nextAbs === hoverPreview?.startAbs ? "start" : "cont",
                            startAbs: hoverPreview?.startAbs,
                          }
                        : paintMap.get(nextAbs)
                      : null;

                  const sameAsPrev = !!effectivePainted && !!prevEff && effectivePainted.startAbs === prevEff.startAbs;
                  const sameAsNext = !!effectivePainted && !!nextEff && effectivePainted.startAbs === nextEff.startAbs;

                  const baseBg = state === "start" ? COLOR_START : state === "cont" ? COLOR_CONT : COLOR_EMPTY;

                  const inHoverRange = isInPreview;
                  const isHoverStart = hoverPreview?.startAbs === abs;

                  const hoverBg =
                    hoverPreview?.mode === "remove"
                      ? isHoverStart
                        ? HOVER_REMOVE_START
                        : HOVER_REMOVE_CONT
                      : hoverPreview?.mode === "add"
                      ? isHoverStart
                        ? HOVER_ADD_START
                        : HOVER_ADD_CONT
                      : null;

                  const background = isDisabledStart
                    ? DISABLED_BG
                    : inHoverRange && hoverBg
                    ? hoverBg
                    : baseBg;

                  // cont cells are light => dark text
                  const wantsDarkText = isDisabledStart || state === "cont" || (!state && !inHoverRange);
                  const color = wantsDarkText ? (isDisabledStart ? DISABLED_TEXT : "#111827") : "#ffffff";

                  const cursor = readOnly ? "not-allowed" : isDisabledStart ? "not-allowed" : "pointer";

                  const title = (() => {
                    if (readOnly) return day.toUpperCase();
                    if (isDisabledStart) return "Not available";
                    if (hoverPreview?.mode === "remove" && inHoverRange) return "Click to remove this instance";
                    if (hoverPreview?.mode === "add" && inHoverRange) return "Click to add";
                    return day.toUpperCase();
                  })();

                  const borderRadius = (() => {
                    const left = sameAsPrev ? 0 : 4;
                    const right = sameAsNext ? 0 : 4;
                    return `${left}px ${right}px ${right}px ${left}px`;
                  })();

                  // keep constant GAP, but visually "bridge" it with shadow so width never changes
                  const bridgeToRight = sameAsNext;
                  const bridgeShadow = bridgeToRight ? `${GAP}px 0 0 0 ${background}` : "none";

                  return (
                    <button
                      key={day}
                      type="button"
                      disabled={readOnly}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (readOnly) return;
                        if (isDisabledStart) return;
                        handleCellClick(abs);
                      }}
                      style={{
                        width: CELL,
                        height: CELL,
                        borderRadius,
                        border: "none",
                        background,
                        color,
                        fontWeight: 400,
                        fontSize: 13,
                        cursor,
                        padding: 0,
                        opacity: isDisabledStart ? 0.75 : 1,

                        // no extra borders/outlines on hover
                        outline: "none",
                        boxShadow: bridgeShadow,

                        transition: "background 80ms linear, opacity 80ms linear, color 80ms linear",
                      }}
                      title={title}
                    >
                      {DAY_ABBR[dayIndex]}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {!readOnly && totalDays > 0 && (
        <div style={{ marginTop: 12 }}>
          <Button
            block
            onClick={showResetToggle ? handleResetSelection : handleAddAllDays}
            disabled={showResetToggle && !hasSelection}
          >
            {showResetToggle ? "Reset selection" : "Add to all days"}
          </Button>
        </div>
      )}

      <div
        style={{
          height: 1,
          background: "#6C6C8929",
          margin: "12px -12px 0",
        }}
      />
    </Modal>
  );
}
